import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Plus, Trash, MapPin, FloppyDisk } from "@phosphor-icons/react";

const TIPOS_ENCARGO = [
  { k: "presencial",     label: "Presencial" },
  { k: "videoperitacion", label: "Videoperitación" },
  { k: "telematico",     label: "Telemático" },
  { k: "juicio",         label: "Juicio" },
];

const TIPO_HONORARIOS_OPTS = [
  { v: "pago_aseguradora_despacho", t: "Pago de la Aseguradora al Despacho" },
  { v: "pago_despacho_perito", t: "Pago del Despacho al Perito" },
  { v: "pago_aseguradora_perito", t: "Pago directo Aseguradora a Perito" },
  { v: "otro", t: "Otro" },
];

const COLUMNS = [
  { k: "desde_tasado",     l: "Desde Tasado",  unit: "€", w: "w-28" },
  { k: "hasta_tasado",     l: "Hasta",         unit: "€", w: "w-28" },
  { k: "fijo",             l: "Fijo",          unit: "€", w: "w-24" },
  { k: "pct_exceso",       l: "% Exceso",      unit: "%", w: "w-24" },
  { k: "pct_tasado",       l: "% Tasado",      unit: "%", w: "w-24" },
  { k: "min_minuta",       l: "Mín minuta",    unit: "€", w: "w-28" },
  { k: "max_minuta",       l: "Máx minuta",    unit: "€", w: "w-28" },
  { k: "pct_corte",        l: "% Corte",       unit: "%", w: "w-24" },
  { k: "por_reclamacion",  l: "Por reclam.",   unit: "€", w: "w-28" },
  { k: "por_concurrencia", l: "Por concurr.",  unit: "€", w: "w-28" },
];


export default function BaremoEditor() {
  const { aseguradoraId, baremoId } = useParams();
  const navigate = useNavigate();
  const [aseg, setAseg] = useState(null);
  const [baremo, setBaremo] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [tab, setTab] = useState("presencial");
  const [tipoHon, setTipoHon] = useState("pago_aseguradora_despacho");
  const [editingMeta, setEditingMeta] = useState(false);
  const [meta, setMeta] = useState({ name: "", descripcion: "", provincias: "", precio_km: "" });

  const load = async () => {
    const [a, b, l] = await Promise.all([
      api.get(`/aseguradoras/${aseguradoraId}`),
      api.get(`/baremos/${baremoId}`),
      api.get(`/baremos/${baremoId}/lineas`),
    ]);
    setAseg(a.data);
    setBaremo(b.data);
    setLineas(l.data);
    setMeta({
      name: b.data.name,
      descripcion: b.data.descripcion || "",
      provincias: (b.data.provincias || []).join(", "),
      precio_km: b.data.precio_km != null ? String(b.data.precio_km) : "",
    });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [baremoId]);

  const saveMeta = async () => {
    const provs = meta.provincias.split(",").map((s) => s.trim()).filter(Boolean);
    const precio_km_num = meta.precio_km.trim() === "" ? null : parseFloat(meta.precio_km);
    await api.put(`/baremos/${baremoId}`, {
      name: meta.name,
      descripcion: meta.descripcion || null,
      provincias: provs,
      precio_km: precio_km_num,
    });
    setEditingMeta(false);
    load();
  };

  const addLinea = async () => {
    const last = filtered[filtered.length - 1];
    const desde = last?.hasta_tasado != null ? Number(last.hasta_tasado) + 0.01 : 0;
    await api.post(`/baremos/${baremoId}/lineas`, {
      baremo_id: baremoId,
      tipo_encargo: tab,
      tipo_honorarios: tipoHon,
      desde_tasado: desde,
      orden: filtered.length,
    });
    load();
  };

  const updateLinea = async (id, field, val) => {
    // Optimistic update
    setLineas((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: val } : l)));
    const num = val === "" ? null : Number(val);
    if (val !== "" && Number.isNaN(num)) return;
    await api.put(`/baremos/lineas/${id}`, { [field]: num });
  };

  const deleteLinea = async (id) => {
    if (!confirm("¿Eliminar esta línea?")) return;
    await api.delete(`/baremos/lineas/${id}`);
    load();
  };

  if (!baremo || !aseg) return <div className="p-10 text-muted text-sm">Cargando...</div>;

  const filtered = lineas
    .filter((l) => l.tipo_encargo === tab && l.tipo_honorarios === tipoHon)
    .sort((a, b) => (a.orden || 0) - (b.orden || 0));

  const tabCount = (k) => lineas.filter((l) => l.tipo_encargo === k).length;

  return (
    <div className="flex flex-col h-screen">
      <header className="border-b border-border bg-white flex-shrink-0">
        <div className="px-6 py-3 flex items-start justify-between gap-6">
          <div className="flex items-start gap-4 min-w-0">
            <button
              onClick={() => navigate(`/admin/aseguradoras/${aseguradoraId}`)}
              className="text-muted hover:text-foreground p-1 mt-0.5"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4" weight="bold" />
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted">
                {aseg.name} · Baremo
              </div>
              {editingMeta ? (
                <div className="mt-1 grid grid-cols-1 gap-2 max-w-2xl">
                  <input
                    value={meta.name}
                    onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                    placeholder="Nombre"
                    className="!text-base !font-bold !py-1"
                  />
                  <input
                    value={meta.descripcion}
                    onChange={(e) => setMeta({ ...meta, descripcion: e.target.value })}
                    placeholder="Descripción"
                    className="!text-xs !py-1"
                  />
                  <input
                    value={meta.provincias}
                    onChange={(e) => setMeta({ ...meta, provincias: e.target.value })}
                    placeholder="Provincias separadas por comas"
                    className="!text-xs !py-1"
                  />
                  <label className="flex items-center gap-2 text-xs">
                    <span className="text-muted">Desplazamiento €/km:</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={meta.precio_km}
                      onChange={(e) => setMeta({ ...meta, precio_km: e.target.value })}
                      placeholder="Vacío = no se paga"
                      data-testid="baremo-precio-km"
                      className="!text-xs !py-1 !max-w-[160px]"
                    />
                    <span className="text-[10px] text-muted italic">Solo presencial / juicio</span>
                  </label>
                </div>
              ) : (
                <>
                  <h1 className="font-display font-bold text-lg tracking-tight">{baremo.name}</h1>
                  {baremo.descripcion && (
                    <div className="text-xs text-muted mt-0.5">{baremo.descripcion}</div>
                  )}
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {(baremo.provincias || []).length === 0 ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-surface text-muted font-medium">
                        Todas las provincias
                      </span>
                    ) : (
                      baremo.provincias.map((p) => (
                        <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-surface text-muted font-medium inline-flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5" weight="bold" />{p}
                        </span>
                      ))
                    )}
                    {baremo.precio_km != null && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 font-medium inline-flex items-center gap-1" data-testid="baremo-precio-km-badge">
                        🚗 {baremo.precio_km.toFixed(2)} €/km · presencial &amp; juicio
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {editingMeta ? (
            <div className="flex gap-2">
              <button onClick={() => setEditingMeta(false)} className="text-xs px-3 py-1.5 text-muted">Cancelar</button>
              <button onClick={saveMeta} className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5">
                <FloppyDisk className="w-3.5 h-3.5" weight="bold" /> Guardar
              </button>
            </div>
          ) : (
            <button onClick={() => setEditingMeta(true)} className="text-xs text-muted hover:text-foreground underline">
              Editar datos
            </button>
          )}
        </div>

        {/* Tabs tipos encargo */}
        <div className="px-6 flex items-center gap-0">
          {TIPOS_ENCARGO.map((t) => {
            const isActive = tab === t.k;
            const c = tabCount(t.k);
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`tipo-${t.k}`}
                className={`px-4 py-2.5 text-[13px] font-medium border-b-2 inline-flex items-center gap-1.5 transition-colors ${
                  isActive ? "border-foreground text-foreground" : "border-transparent text-muted hover:text-foreground"
                }`}
              >
                {t.label}
                {c > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${isActive ? "bg-foreground text-white" : "bg-surface text-muted"}`}>
                    {c}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto bg-surface p-6">
        <div className="bg-white border border-border rounded-md p-5 max-w-[1200px]">
          {/* Selector tipo honorarios */}
          <div className="flex items-end justify-between gap-4 mb-4">
            <div className="max-w-sm w-full">
              <label htmlFor="tipo_honorarios">Tipo de honorarios</label>
              <select
                id="tipo_honorarios"
                value={tipoHon}
                onChange={(e) => setTipoHon(e.target.value)}
                data-testid="tipo-honorarios-select"
              >
                {TIPO_HONORARIOS_OPTS.map((o) => (
                  <option key={o.v} value={o.v}>{o.t}</option>
                ))}
              </select>
            </div>
            <button
              onClick={addLinea}
              data-testid="add-linea"
              className="bg-foreground text-white text-xs px-3 py-1.5 rounded-md font-medium inline-flex items-center gap-1.5 whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" weight="bold" /> Nueva línea
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-collapse">
              <thead>
                <tr className="bg-surface text-[10px] uppercase tracking-widest text-muted">
                  {COLUMNS.map((c) => (
                    <th key={c.k} className={`text-right py-2 px-2 font-semibold ${c.w}`}>
                      {c.l} <span className="text-muted/70 normal-case tracking-normal font-normal">({c.unit})</span>
                    </th>
                  ))}
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={COLUMNS.length + 1} className="py-8 text-center text-muted text-xs">
                    Sin líneas para "{TIPOS_ENCARGO.find(t => t.k === tab)?.label}". Pulsa "Nueva línea".
                  </td></tr>
                )}
                {filtered.map((l) => (
                  <tr key={l.id} className="border-t border-border/60 hover:bg-surface/40">
                    {COLUMNS.map((c) => (
                      <td key={c.k} className="py-1 px-1">
                        <input
                          type="number"
                          step="0.01"
                          value={l[c.k] ?? ""}
                          onChange={(e) => updateLinea(l.id, c.k, e.target.value)}
                          data-testid={`linea-${l.id}-${c.k}`}
                          className="!text-right !py-1 !px-2 !text-[13px] tabular-nums"
                          placeholder="0"
                        />
                      </td>
                    ))}
                    <td className="px-2 text-center">
                      <button
                        onClick={() => deleteLinea(l.id)}
                        data-testid={`del-linea-${l.id}`}
                        className="p-1 text-muted hover:text-status-error-fg"
                      >
                        <Trash className="w-3.5 h-3.5" weight="bold" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hint */}
        <div className="mt-4 max-w-[1200px] text-[11px] text-muted leading-relaxed">
          💡 <strong className="text-foreground">Tramos por tasación:</strong> rellena las columnas que aplican.
          La línea más típica usa "Desde-Hasta" + ("Fijo" o "% Tasado") + "Mín/Máx minuta".
          Las columnas "% Corte", "Por reclamación" y "Por concurrencia" son extras opcionales.
        </div>
      </div>
    </div>
  );
}
